import { isMosaicTrafficLightsValue, MosaicTrafficLightsValue } from "../../traffic-lights";
import { AitCellJustifyText } from "./interface";

/**
 * Data for an individual cell.
 *
 * @template T The data type used for cell text and comments.
 */

export interface AitCellData<T> {
  /** Unique identifier for the cell. */
  aitid: string;
  /** The base text content for the cell. */
  text: T;
  /** Optional text justification for rendering. */
  justifyText?: AitCellJustifyText;
  /** Comments associated with the cell. */
  comments: T;
  /** The column identifier this cell belongs to. */
  columnId: string;
  /** Optional width for the column in pixels. */
  colWidth?: number;
  /** Number of spaces or indents before the cell text. */
  textIndents?: number;
  /** Text after list/replacement processing that is shown to the user. */
  replacedText?: T;
  /** When true, insert a blank row after this repeat. */
  spaceAfterRepeat?: boolean;
  /** Traffic light value for the cell's core status. */
  coreTrafficLight: MosaicTrafficLightsValue;
  /** Equivalent aitid when this cell originates from a template. */
  templateAitId?: string;
  /** Equivalent aitid for a template column cell header. */
  templateColumnAitId?: string;
  /** When true, show the per-cell options UI. */
  showCellOptions?: boolean;
  /** When true, the cell is read-only. */
  readOnly?: boolean;
}

/**
 * Type guard that asserts whether a value conforms to the AitCellData shape.
 *
 * @param obj - The object to check
 * @param isT - Type guard for the generic type T
 * @param noColumnIdCheck - Optional flag to skip column ID checks (used for row templates)
 * @returns boolean
 */
export const isAitCellData = <T>(
  obj: unknown,
  isT: (obj: unknown) => boolean,
  noColumnIdCheck?: boolean,
): obj is AitCellData<T> => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;
  // Check basic property
  const chkObj = obj as AitCellData<T>;
  if (typeof chkObj.aitid !== "string") return false;
  if (!isT(chkObj.text)) return false;
  if (!isT(chkObj.comments)) return false;
  if (!noColumnIdCheck && typeof chkObj.columnId !== "string") return false;
  if (typeof chkObj.colWidth !== "undefined" && typeof chkObj.colWidth !== "number") return false;
  if (typeof chkObj.textIndents !== "undefined" && typeof chkObj.textIndents !== "number")
    return false;
  if (chkObj.replacedText !== undefined && !isT(chkObj.replacedText)) return false;
  if (
    typeof chkObj.spaceAfterRepeat !== "undefined" &&
    typeof chkObj.spaceAfterRepeat !== "boolean"
  )
    return false;
  if (typeof chkObj.templateAitId !== "undefined" && typeof chkObj.templateAitId !== "string")
    return false;
  if (
    typeof chkObj.templateColumnAitId !== "undefined" &&
    typeof chkObj.templateColumnAitId !== "string"
  )
    return false;
  if (typeof chkObj.showCellOptions !== "undefined" && typeof chkObj.showCellOptions !== "boolean")
    return false;
  if (typeof chkObj.readOnly !== "undefined" && typeof chkObj.readOnly !== "boolean") return false;
  // Check types
  if (
    chkObj.justifyText !== undefined &&
    !["left", "center", "right", "justify", "decimal", "default"].includes(chkObj.justifyText)
  )
    return false;
  if (!isMosaicTrafficLightsValue(chkObj.coreTrafficLight)) return false;

  // Everything is good
  return true;
};
